import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import {
  ApAgingBucketKind,
  ApAgingDocument,
  ApAgingCell,
  ApAgingExportKind,
  ApAgingProviderGroup,
  ApAgingReport,
} from '../../../models/ap-aging/ap-aging';
import { ApAgingProxyService } from '../../../proxies/ap-aging.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { currencyLabel, currencySymbol } from '../../../utilities/currency.util';

/**
 * The weekly AP payment projection: what has to go out, to whom, and when.
 *
 * One row per provider showing what each payment week owes them; expanding a row reveals the documents
 * behind it, rendered as rows of the SAME table so their amounts stay under the week they fall in. Two
 * reports in one grid â€” the by-provider summary and the document detail.
 *
 * Weeks OPEN on their Wednesday and run seven days. Overdue is its own column rather than being folded into
 * week one, which is what the spreadsheet this replaces did â€” making half the balance read as due now when
 * much of it was years old.
 */
/**
 * Cap on the placeholder rows shown while a provider's documents load. A provider with two hundred open
 * documents does not need two hundred skeletons to communicate "loading".
 */
const MAX_SKELETON_ROWS = 8;

@Component({
  selector: 'app-ap-aging',
  templateUrl: './ap-aging.component.html',
  styleUrl: './ap-aging.component.scss',
})
export class ApAgingComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly proxy = inject(ApAgingProxyService);
  private readonly locale = inject(LocaleHelperService);
  private readonly translate = inject(TranslateService);

  readonly isLoading = signal(0);
  readonly report = signal<ApAgingReport | null>(null);

  /**
   * The symbol every figure in the grid is prefixed with.
   *
   * <p>Read off the report rather than hardcoded: every amount here is stated in the company's FUNCTIONAL
   * currency (each document converted at its own booked rate), so the symbol that marks them is whatever
   * that currency is. It is the dollar today, and this stays right on the day a company keeps its books in
   * something else.</p>
   */
  readonly reportingSymbol = computed(() => currencySymbol(this.report()?.reportingCurrency));

  /**
   * A currency's code, for naming one that is NOT the reporting currency.
   *
   * <p>The source-amount line under a converted figure used to render the raw enum, so an HNL invoice
   * announced itself as "1" â€” the enum's ordinal, not its name.</p>
   */
  currencyCode(currency: number | null | undefined): string {
    return currencyLabel(currency);
  }

  /** Defaults to the current week; the API snaps whatever we send to that week's Wednesday. */
  readonly anchorDate = signal<Date>(new Date());
  readonly weeks = signal<number>(8);

  /** Empty means "every provider" â€” the filter narrows, it never hides by default. */
  readonly selectedProviders = signal<string[]>([]);

  /** Only one provider opens at a time, so the detail stays readable on a grid this wide. */
  readonly expandedProvider = signal<string | null>(null);

  /**
   * Documents per provider, fetched when a row is first opened.
   *
   * The summary deliberately ships no document rows â€” loading every open payable in the company up front
   * to satisfy a drill-down the user may never perform is the cost the split avoids. Cached here so
   * re-opening a provider is free; cleared whenever the anchor or week count changes, since the documents
   * are bucketed on that grid and would otherwise be stale.
   */
  private readonly documentCache = signal<Record<string, ApAgingDocument[]>>({});
  readonly loadingProvider = signal<string | null>(null);

  /**
   * Sort state. Encoded as a string because the bucket columns are not fields on the row â€” their amounts
   * live in `cells[]` keyed by bucket index, so there is no field path for PrimeNG's built-in sorting to
   * follow and the comparison has to be resolved per column kind. `provider` | `total` | `bucket:<index>`.
   */
  readonly sortField = signal<string>('provider');
  readonly sortOrder = signal<1 | -1>(1);

  /** Every day except Wednesday â€” the picker offers Wednesdays only, since a week opens on one. */
  readonly nonWednesdays = [0, 1, 2, 4, 5, 6];

  readonly weekOptions = [4, 8, 12, 16].map((v) => ({ label: `${v}`, value: v }));

  readonly columns = computed<ApAgingCell[]>(() => this.report()?.totals ?? []);

  /** Options for the provider filter, drawn from the providers that actually have open payables. */
  readonly providerOptions = computed(() =>
    (this.report()?.providerGroups ?? []).map((g) => ({
      label: g.providerName || this.t('ApAging_OneTimeVendor', 'One-time vendor'),
      value: g.providerReference,
    })),
  );

  readonly groups = computed<ApAgingProviderGroup[]>(() => {
    const all = this.report()?.providerGroups ?? [];
    const selected = this.selectedProviders();
    const filtered = selected.length === 0 ? all : all.filter((g) => selected.includes(g.providerReference));

    const field = this.sortField();
    const order = this.sortOrder();

    // Copy before sorting â€” the computed must not reorder the array the signal is holding.
    return [...filtered].sort((a, b) => {
      if (field === 'provider') {
        return order * this.providerLabel(a).localeCompare(this.providerLabel(b), undefined, { sensitivity: 'base' });
      }

      const av = this.sortValue(a, field);
      const bv = this.sortValue(b, field);
      // Ties fall back to provider name, so an amount sort over many empty buckets still reads in a
      // stable, predictable order rather than whatever the source happened to hold.
      return av === bv ? this.providerLabel(a).localeCompare(this.providerLabel(b), undefined, { sensitivity: 'base' }) : order * (av - bv);
    });
  });

  /** The number a non-provider column sorts on. An absent bucket sorts as zero, which is what it shows. */
  private sortValue(group: ApAgingProviderGroup, field: string): number {
    if (field === 'total') return group.subtotalAmount;
    const index = Number(field.slice('bucket:'.length));
    return this.groupCell(group, index)?.amount ?? 0;
  }

  /** Column key for a bucket, matching the encoding `sortField` uses. */
  bucketField(cell: ApAgingCell): string {
    return `bucket:${cell.index}`;
  }

  /** First click sorts a new column ascending; clicking the active column flips it. */
  onSort(field: string): void {
    if (this.sortField() === field) {
      this.sortOrder.update((o) => (o === 1 ? -1 : 1));
      return;
    }
    this.sortField.set(field);
    this.sortOrder.set(1);
  }

  isSorted(field: string): boolean {
    return this.sortField() === field;
  }

  sortIcon(field: string): string {
    if (!this.isSorted(field)) return 'pi-sort-alt';
    return this.sortOrder() === 1 ? 'pi-sort-amount-up-alt' : 'pi-sort-amount-down';
  }

  /** Announced to assistive tech so the sorted column is not conveyed by the icon alone. */
  ariaSort(field: string): 'ascending' | 'descending' | 'none' {
    if (!this.isSorted(field)) return 'none';
    return this.sortOrder() === 1 ? 'ascending' : 'descending';
  }

  readonly isFiltered = computed(() => this.selectedProviders().length > 0);

  /**
   * Column totals for what is on screen. Recomputed from the visible groups rather than read off the
   * report, so a filtered view foots to what the user can actually see â€” a footer showing the unfiltered
   * total under a filtered table reads as an arithmetic error.
   */
  readonly totals = computed<ApAgingCell[]>(() => {
    const columns = this.columns();
    if (!this.isFiltered()) return columns;

    const visible = this.groups();
    return columns.map((col) => {
      const cells = visible.map((g) => g.cells.find((c) => c.index === col.index));
      return {
        ...col,
        amount: this.sum(cells.map((c) => c?.amount ?? 0)),
        documentCount: cells.reduce((n, c) => n + (c?.documentCount ?? 0), 0),
        blockedDocumentCount: cells.reduce((n, c) => n + (c?.blockedDocumentCount ?? 0), 0),
        blockedAmount: this.sum(cells.map((c) => c?.blockedAmount ?? 0)),
      };
    });
  });

  readonly grandTotal = computed(() =>
    this.isFiltered() ? this.sum(this.groups().map((g) => g.subtotalAmount)) : (this.report()?.grandTotal ?? 0),
  );

  ngOnInit(): void {
    const qp = this.route.snapshot.queryParamMap;

    const anchor = qp.get('anchorDate');
    if (anchor) {
      const parsed = new Date(anchor);
      if (!Number.isNaN(parsed.getTime())) this.anchorDate.set(parsed);
    }

    const weeks = Number(qp.get('weeks'));
    if (Number.isInteger(weeks) && weeks >= 1 && weeks <= 26) this.weeks.set(weeks);

    this.load();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v === key ? fallback : v;
  }

  /** Cents in, cents out â€” avoids float drift showing up as a footer that misses by 0.01. */
  private sum(values: number[]): number {
    return Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100;
  }

  private load(): void {
    // The cached documents were bucketed on the previous grid, so they cannot survive a reload.
    this.documentCache.set({});
    this.isLoading.update((v) => v + 1);
    this.proxy
      .getAging({ anchorDate: this.toIsoDate(this.anchorDate()), weeks: this.weeks() })
      .pipe(finalize(() => this.isLoading.update((v) => v - 1)))
      .subscribe({
        next: (response) => {
          this.report.set(response.data ?? null);
          // A provider that survived the reload keeps its row open; one that did not must not leave a
          // stale expansion behind.
          const open = this.expandedProvider();
          if (open && !(response.data?.providerGroups ?? []).some((g) => g.providerReference === open)) {
            this.expandedProvider.set(null);
          }
        },
        error: () => {
          this.report.set(null);
          this.expandedProvider.set(null);
        },
      });
  }

  /** Date only â€” the API works in calendar days, and sending a UTC instant can shift the day. */
  private toIsoDate(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  /**
   * Both grid controls refetch, so both check first that the grid actually moved.
   *
   * <p>A PrimeNG control can hand back the value it was just given rather than one the user chose â€” the
   * week `p-select` did exactly that on page open until `autoDisplayFirst` was turned off â€” and a user
   * re-picking the date already showing is a no-op too. Either way a reload would drop the document cache
   * and re-query for a grid that did not change, so the equality check stays even though the echo is
   * fixed at the template.</p>
   */
  onAnchorChange(date: Date): void {
    // Compared as calendar days: the picker hands back a fresh Date, so identity tells us nothing.
    if (!date || this.toIsoDate(date) === this.toIsoDate(this.anchorDate())) return;

    this.anchorDate.set(date);
    this.syncQueryParams();
    this.load();
  }

  onWeeksChange(weeks: number): void {
    if (weeks == null || weeks === this.weeks()) return;

    this.weeks.set(weeks);
    this.syncQueryParams();
    this.load();
  }

  /** Documents already fetched for a provider, or an empty list while one is still loading. */
  documentsFor(group: ApAgingProviderGroup): ApAgingDocument[] {
    return this.documentCache()[group.providerReference] ?? [];
  }

  isLoadingDocuments(group: ApAgingProviderGroup): boolean {
    return this.loadingProvider() === group.providerReference;
  }

  /**
   * Placeholder rows to hold the space the documents are about to fill.
   *
   * The summary already told us how many documents are coming, so the placeholder lands close to the real
   * height and the providers below barely shift when the response arrives.
   */
  skeletonRows(group: ApAgingProviderGroup): number[] {
    const count = Math.min(Math.max(group.documentCount, 1), MAX_SKELETON_ROWS);
    return Array.from({ length: count }, (_, index) => index);
  }

  private loadDocuments(group: ApAgingProviderGroup): void {
    const key = group.providerReference;
    if (this.documentCache()[key]) return; // already fetched for this anchor/week grid

    // Deliberately NOT counted into `isLoading`: that drives the full-page overlay, which is right when
    // the page has nothing to show, and wrong here â€” the summary is already on screen and readable. The
    // wait belongs in the rows the documents are about to occupy, so the rest of the report stays usable.
    this.loadingProvider.set(key);
    this.proxy
      .getProviderDocuments({
        anchorDate: this.toIsoDate(this.anchorDate()),
        weeks: this.weeks(),
        providerReference: key,
      })
      .pipe(
        finalize(() => {
          if (this.loadingProvider() === key) this.loadingProvider.set(null);
        }),
      )
      .subscribe({
        // The API returns them in due-date order; cached as-is so the detail reads as a schedule.
        next: (response) => this.documentCache.update((cache) => ({ ...cache, [key]: response.data?.documents ?? [] })),
        error: () => this.expandedProvider.set(null),
      });
  }

  onProvidersChange(providers: string[]): void {
    this.selectedProviders.set(providers ?? []);
    // Filtering is client-side, so nothing is refetched â€” but an open row that just got filtered out
    // would otherwise stay expanded behind the scenes.
    const open = this.expandedProvider();
    if (open && providers?.length && !providers.includes(open)) this.expandedProvider.set(null);
  }

  clearProviders(): void {
    this.selectedProviders.set([]);
  }

  toggleProvider(group: ApAgingProviderGroup): void {
    const next = this.expandedProvider() === group.providerReference ? null : group.providerReference;
    this.expandedProvider.set(next);
    if (next !== null) this.loadDocuments(group);
  }

  isExpanded(group: ApAgingProviderGroup): boolean {
    return this.expandedProvider() === group.providerReference;
  }

  /** Keeps the view shareable â€” the same convention the subledger uses. */
  private syncQueryParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { anchorDate: this.toIsoDate(this.anchorDate()), weeks: this.weeks() },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  providerLabel(group: ApAgingProviderGroup): string {
    return group.providerName || this.t('ApAging_OneTimeVendor', 'One-time vendor');
  }

  accountLabel(accountName: unknown, accountNumber: string): string {
    const name = this.locale.getLocalizedValue(accountName as never);
    return name ? `${accountNumber} Â· ${name}` : accountNumber;
  }

  /** Column heading: "Overdue", "Later", or the week's opening Wednesday. */
  columnLabel(cell: ApAgingCell): string {
    switch (cell.kind) {
      case ApAgingBucketKind.Overdue:
        return this.t('ApAging_Overdue', 'Overdue');
      case ApAgingBucketKind.Beyond:
        return this.t('ApAging_Beyond', 'Later');
      default:
        return cell.weekStarting ? new Date(cell.weekStarting).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }) : '';
    }
  }

  groupCell(group: ApAgingProviderGroup, index: number): ApAgingCell | undefined {
    return group.cells.find((c) => c.index === index);
  }

  isOverdueColumn(cell: ApAgingCell): boolean {
    return cell.kind === ApAgingBucketKind.Overdue;
  }

  blockedTooltip(reason: string | null): string {
    return reason ?? this.t('ApAging_BlockedTooltip', 'Awaiting a withholding certificate');
  }

  readonly isExporting = signal(false);

  /**
   * The two workbooks, as a menu rather than a single button.
   *
   * Neither view is the obvious default â€” a treasury planner wants the provider summary, whoever is
   * reconciling against the control account wants the documents â€” so a split button's primary action would
   * be a coin toss that half the users click by mistake. A menu makes both an explicit choice.
   */
  readonly exportMenuItems = computed<MenuItem[]>(() => [
    {
      label: this.t('ApAging_ExportByProvider', 'By provider'),
      icon: 'pi pi-users',
      command: () => this.exportAs(ApAgingExportKind.ByProvider),
    },
    {
      label: this.t('ApAging_ExportByDocument', 'By document'),
      icon: 'pi pi-file',
      command: () => this.exportAs(ApAgingExportKind.ByDocument),
    },
  ]);

  /**
   * Renders one of the workbooks server-side and hands it to the browser.
   *
   * <p>Built by the API, not from what the table happens to be holding: the client only has documents for
   * providers the user expanded, so a client-side file would silently omit the rest. Sends the filter too,
   * so the workbook matches the table it was exported from.</p>
   */
  private exportAs(kind: ApAgingExportKind): void {
    if (this.isExporting()) return;

    this.isExporting.set(true);
    this.proxy
      .exportAging({
        anchorDate: this.toIsoDate(this.anchorDate()),
        weeks: this.weeks(),
        kind,
        providerReferences: this.selectedProviders(),
      })
      .pipe(finalize(() => this.isExporting.set(false)))
      .subscribe({
        next: (blob) => this.triggerDownload(blob, this.fileName(kind)),
      });
  }

  private fileName(kind: ApAgingExportKind): string {
    const view = kind === ApAgingExportKind.ByDocument ? 'by-document' : 'by-provider';
    const anchor = this.report()?.anchorDate?.slice(0, 10) ?? this.toIsoDate(this.anchorDate());
    return `ap-aging_${view}_${anchor}.xlsx`;
  }

  private triggerDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }
}
